import {
  AreaChart,
  BarChart,
  DonutChart,
  LineChart,
  PieChart,
  RadarChart,
} from "@mantine/charts";
import { Text } from "@mantine/core";

type ChartType = "line" | "bar" | "area" | "pie" | "donut" | "radar";

interface ChartSeriesSpec {
  name: string;
  label?: string;
  color?: string;
}

interface ChartSpec {
  type: ChartType;
  title?: string;
  xKey: string;
  series: ChartSeriesSpec[];
  data: Array<Record<string, unknown>>;
}

const PALETTE = [
  "violet.6",
  "teal.6",
  "blue.6",
  "grape.6",
  "orange.6",
  "red.6",
  "cyan.6",
  "lime.6",
  "pink.6",
  "indigo.6",
];

const CARTESIAN: ChartType[] = ["line", "bar", "area", "radar"];
const CIRCULAR: ChartType[] = ["pie", "donut"];
const ALL_TYPES: ChartType[] = [...CARTESIAN, ...CIRCULAR];

/**
 * Renders a chart from the JSON the assistant emits in a `tma-chart` fenced
 * code block. Supports line, bar, area, radar (cartesian) and pie, donut
 * (circular). All validation lives here so callers never need to guard.
 */
export const AssistantChart = ({ raw }: { raw: string }) => {
  const spec = parseSpec(raw);
  if (!spec) {
    return (
      <Text size="xs" c="dimmed" className="italic">
        (chart unavailable)
      </Text>
    );
  }

  return (
    <div className="my-2 rounded-lg border border-gray-200 bg-white p-3">
      {spec.title && (
        <Text size="sm" fw={600} className="mb-2 text-slate-700">
          {spec.title}
        </Text>
      )}
      {renderChart(spec)}
    </div>
  );
};

const renderChart = (spec: ChartSpec) => {
  const series = spec.series.map((s, i) => ({
    name: s.name,
    label: s.label ?? s.name,
    color: s.color ?? PALETTE[i % PALETTE.length],
  }));
  const withLegend = series.length > 1;

  switch (spec.type) {
    case "bar":
      return (
        <BarChart
          h={220}
          data={spec.data}
          dataKey={spec.xKey}
          series={series}
          tickLine="y"
          gridAxis="y"
          withLegend={withLegend}
        />
      );
    case "area":
      return (
        <AreaChart
          h={220}
          data={spec.data}
          dataKey={spec.xKey}
          series={series}
          curveType="monotone"
          withLegend={withLegend}
        />
      );
    case "radar":
      return (
        <RadarChart
          h={260}
          data={spec.data}
          dataKey={spec.xKey}
          series={series}
          withPolarGrid
          withPolarAngleAxis
          withPolarRadiusAxis
        />
      );
    case "pie":
      return (
        <PieChart
          h={240}
          data={toCircularData(spec)}
          withLabels
          withLabelsLine
          withTooltip
        />
      );
    case "donut":
      return (
        <DonutChart
          h={240}
          data={toCircularData(spec)}
          withLabels
          withTooltip
        />
      );
    case "line":
    default:
      return (
        <LineChart
          h={220}
          data={spec.data}
          dataKey={spec.xKey}
          series={series}
          curveType="monotone"
          withLegend={withLegend}
        />
      );
  }
};

/** Circular charts want [{name, value, color}] — derive from xKey + first series. */
const toCircularData = (
  spec: ChartSpec
): Array<{ name: string; value: number; color: string }> => {
  const valueKey = spec.series[0]?.name;
  return spec.data
    .map((row, i) => ({
      name: labelOf(row[spec.xKey]),
      value: Number(row[valueKey]),
      color: spec.series[0]?.color ?? PALETTE[i % PALETTE.length],
    }))
    .filter((d) => Number.isFinite(d.value));
};

const labelOf = (value: unknown): string =>
  typeof value === "string"
    ? value
    : typeof value === "number" || typeof value === "boolean"
      ? String(value)
      : "";

const parseSpec = (raw: string): ChartSpec | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const spec = parsed as Partial<ChartSpec>;
  const type = ALL_TYPES.find((t) => t === spec.type);
  if (!type) {
    return null;
  }
  if (typeof spec.xKey !== "string" || !spec.xKey) {
    return null;
  }
  if (!Array.isArray(spec.series) || spec.series.length === 0) {
    return null;
  }
  if (
    !spec.series.every(
      (s) => typeof s === "object" && s !== null && typeof s.name === "string"
    )
  ) {
    return null;
  }
  if (!Array.isArray(spec.data) || spec.data.length === 0) {
    return null;
  }
  return {
    type,
    title: typeof spec.title === "string" ? spec.title : undefined,
    xKey: spec.xKey,
    series: spec.series,
    data: spec.data,
  };
};
